import {useVorderState} from "@/modules/shop/store/vorder-state";
import {useVorderTotalStore} from "@/modules/shop/store/vorder-total";
import {defineStore, storeToRefs} from "pinia";
import {computed} from "vue";

const STORE_NAME = 'vorder-payment-gift'

export const PAYMENT_GIFT_MIN_ORDER = 2000

export interface PaymentGiftOption {
    code: string
    title: string
    image: string
}

export const useVorderPaymentGift = defineStore(STORE_NAME, () => {

    const vorderState = useVorderState()
    const vorderTotalStore = useVorderTotalStore()

    const {attrs} = storeToRefs(vorderState)
    const {pricePay} = storeToRefs(vorderTotalStore)

    const giftOptions = computed<PaymentGiftOption[]>(() => [
        {
            code: 'fry',
            title: 'Ролл Дуо Фрай',
            image: 'https://irkutsk.sushi-studio.ru/upload/1200x840_MustHave2025_08.jpg',
        },
        {
            code: 'baklagany',
            title: 'Хрустящие баклажаны в азиатском соусе',
            image: 'https://irkutsk.sushi-studio.ru/upload/iblock/daa/1200x840_Menu2025_101-rw600.jpg',
        },
    ])

    const giftOptionsByCode = computed(() => {
        return giftOptions.value.reduce<Record<string, PaymentGiftOption>>((map, option) => {
            map[option.code] = option
            return map
        }, {})
    })

    const isCashPayment = computed(() => attrs.value.PAYMENT_TYPE === 'cash')

    const isEligible = computed(() => pricePay.value >= PAYMENT_GIFT_MIN_ORDER)

    const hasGiftOptions = computed(() => giftOptions.value.length > 0)

    const isSingleGift = computed(() => giftOptions.value.length === 1)

    const singleGift = computed(() => isSingleGift.value ? giftOptions.value[0] : null)

    const isAvailable = computed(() => isEligible.value && hasGiftOptions.value)

    const amountRemaining = computed(() => {
        return Math.max(0, PAYMENT_GIFT_MIN_ORDER - pricePay.value)
    })

    const progressPercent = computed(() => {
        if (PAYMENT_GIFT_MIN_ORDER <= 0) return 100
        return Math.min(100, Math.round((pricePay.value / PAYMENT_GIFT_MIN_ORDER) * 100))
    })

    const selectedGiftCode = computed(() => attrs.value.PAYMENT_GIFT || null)

    const selectedGift = computed(() => {
        const code = selectedGiftCode.value
        if (!code) return null
        return giftOptionsByCode.value[code] || null
    })

    const isGiftSelected = computed(() => !!selectedGift.value)

    const isGiftCodeValid = (code: string | null | undefined): boolean => {
        if (!code) return false
        return !!giftOptionsByCode.value[code]
    }

    const getGiftByCode = (code: string | null | undefined): PaymentGiftOption | null | undefined => {
        if (!isGiftCodeValid(code)) return null
        return giftOptionsByCode.value[code!]
    }

    const selectGift = (code: string) => {
        if (!isGiftCodeValid(code)) return
        vorderState.setAttr('PAYMENT_GIFT', code)
    }

    const clearGift = () => {
        vorderState.setAttr('PAYMENT_GIFT', null)
    }

    const switchToCash = () => {
        vorderState.setAttr('PAYMENT_TYPE', 'cash')
    }

    const syncPaymentGift = () => {
        if (!isCashPayment.value || !isEligible.value) {
            if (selectedGiftCode.value) {
                clearGift()
            }
            return
        }

        if (!isGiftCodeValid(selectedGiftCode.value)) {
            if (giftOptions.value[0]?.code)
                selectGift(giftOptions.value[0]?.code)
        }
    }

    const giftAvailable = computed(() => {
        return isEligible.value && isCashPayment.value
    })

    return {
        giftOptions,
        giftOptionsByCode,
        isCashPayment,
        isEligible,
        hasGiftOptions,
        isSingleGift,
        singleGift,
        isAvailable,
        amountRemaining,
        progressPercent,
        pricePay,
        selectedGiftCode,
        selectedGift,
        isGiftSelected,
        isGiftCodeValid,
        getGiftByCode,
        selectGift,
        clearGift,
        switchToCash,
        syncPaymentGift,
        giftAvailable
    }
})
